/**
 * CifDetail — record page for a CIF (per Bible §7.2 "Record CIF page" P1).
 *
 * Layout: header card with entity chip + CIF id + primary name + Related People
 * strip + Insider chip + nav actions (Back to search, + Add relationship, Edit
 * CIF), followed by a tab bar (Products / Documents / Tasks / History).
 *
 * 2026-04-24 (modal-everywhere pass): the Details tab has been collapsed.
 * "Edit CIF" now opens the SAME `PersonDialog` dispatcher the New Acct flow
 * uses — rendered in `mode="edit"` on top of ModalShell. The dispatcher routes
 * to the correct category dialog (Individual / Business / Trust / Estate /
 * Fiduciary / Government) based on `person.customerType`. No inline surface.
 *
 * Save cascade (CustMod → IdVerifyAdd → CustMsgAdd → contact-change-effects
 * → session events) is preserved — it runs in the dialog's onSave handler
 * below. After save, the modal closes and the CIF reloads.
 */
import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Banner, Button, EntityIcon } from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useCif } from './hooks/useCif';
import { useEmitEvent } from './hooks/useEmitEvent';
import { useApi } from './hooks/useApi';
import { useContactChangeEffects } from './hooks/useContactChangeEffects';
import { ProductsTab } from './tabs/ProductsTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { TasksTab } from './tabs/TasksTab';
import { HistoryTab } from './tabs/HistoryTab';
import { RelatedPeople } from './components/RelatedPeople';
import { InsiderMonitor } from './components/InsiderMonitor';
import { formatSsn } from '@/utils/ssn';
import { formatLocal } from '@/utils/time';
import { useAuth } from '@/contexts/AuthContext';
import { PersonDialog } from '@/modules/RAO/components/personDialog/PersonDialog';
import type { RaoPerson } from '@/modules/RAO/types';
import {
  custInqToRaoPerson,
  raoPersonToCustMod,
  changedCustModKeys,
  ID_CUSTMOD_KEYS,
  CONTACT_CUSTMOD_KEYS,
} from './adapters/custInqToRaoPerson';
import type { ChangeDiffEntry } from './hooks/useChangeTracker';
import { AddressChangeDialog } from './maint-actions/address-change/AddressChangeDialog';
import { useProfileChange } from '@/components/customer/useProfileChange';
import { readProfileChangeMode } from '@/components/customer/profileChange';
import { useConfig } from '@/contexts/ConfigContext';
import { useMaintenanceSwitches, TEST_GATE_MESSAGE } from '@/components/maintenance/useMaintenanceSwitches';
import { evaluateTestIdentity, readTestIdentitySettings } from '@/utils/testIdentity';

// Details tab retired — the full demographic surface lives behind the modal
// "Edit CIF" button. The remaining tabs describe things ABOUT the CIF
// (products held, documents filed, tasks in flight, change history).
type TabId = 'products' | 'documents' | 'tasks' | 'history';

/** CustMod keys the BACKLOG-1675 profile-change ceremony writes itself. */
const PROFILE_CHANGE_CUSTMOD_KEYS: ReadonlyArray<string> = [
  'FirstName', 'MiddleName', 'LastName', 'BirthDt',
  'StreetAddr1', 'City', 'State', 'Zip', 'MailAddr', 'MailCity', 'MailState', 'MailZip',
  'PhoneHome', 'PhoneCell', 'PhoneBus', 'Email', 'OccType', 'EmplName',
];

interface TabDef {
  id: TabId;
  label: string;
}

const TABS: TabDef[] = [
  { id: 'products',  label: 'Products' },
  { id: 'documents', label: 'Documents' },
  { id: 'tasks',     label: 'Tasks' },
  { id: 'history',   label: 'History' },
  // PILOT 2026-09-11 (Bill): File Maint is REMOVED from the tab bar. It was the
  // last doorway into the RAM Account-maintenance surface, which is already
  // hidden from the rail — so hiding the module alone did not close it, exactly
  // like the in-page doorways found in the 09-04 crawl. RAM offers Back on
  // Track and UTMA successor, both still marked "Pending Collette
  // clarification", plus Close account / Block account, none of which are in
  // Phase 1 scope. Restore this line when RAM returns to the rail.
];

import type { ParsedCustInq } from '@/utils/custInqParser';

function pickPrimaryName(inq: ParsedCustInq | null): { name: string; ssn?: string } {
  if (!inq) return { name: '—' };
  // Prefer the business ComName when populated (entity CIFs), otherwise the
  // individual first/last combination produced by the parser. Falls back to a
  // dash so the header never renders undefined.
  const combined = `${inq.firstName} ${inq.lastName}`.replace(/\s+/g, ' ').trim();
  const name = inq.comName || combined || '—';
  const ssn = inq.taxId || undefined;
  return { name, ...(ssn ? { ssn } : {}) };
}

/** Stable text for "did the officer change anything": keys sorted, blank / null / undefined values dropped. */
function comparable(value: unknown): string {
  const norm = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(norm);
    if (v && typeof v === 'object') {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(v as Record<string, unknown>).sort()) {
        const x = (v as Record<string, unknown>)[k];
        if (x === undefined || x === null || x === '') continue;
        out[k] = norm(x);
      }
      return out;
    }
    return v;
  };
  return JSON.stringify(norm(value));
}

export function CifDetail(): JSX.Element {
  const { cifId } = useParams<{ cifId: string }>();
  const navigate = useNavigate();
  const maintSwitches = useMaintenanceSwitches();
  const { data, loading, error, reload } = useCif(cifId);
  const emit = useEmitEvent();
  const { client } = useApi();
  const { orchestrator } = useContactChangeEffects();
  // BACKLOG-1675 (Bill 2026-10-02) — confirm-step profile change: the server
  // diffs the edit against live Core Director, lists the accounts the customer
  // owns with the fields the change flows to, and writes CustMod + AcctMod per
  // confirmed account + 90-day notes once the officer confirms. Admin setting
  // profileChange.mode = commit-time restores the direct CustMod below.
  const { begin: beginProfileChange, modal: profileChangeModal } = useProfileChange();
  const { config } = useConfig();
  const profileMode = readProfileChangeMode(config.systemSettings);
  // Test gate (Bill 2026-10-17 support report): while maint.testCustomersOnly is on, a real customer's record can't be
  // changed here. The server enforces it too (MaintenanceFence); this stops the dialogs opening at all.
  const testGateBlocks = useMemo((): boolean => {
    if (!maintSwitches.testCustomersOnly) return false;
    if (!data) return true;
    const kind = evaluateTestIdentity(
      { firstName: data.firstName, lastName: data.lastName, taxId: data.taxId, cifId: data.cifId || cifId },
      readTestIdentitySettings(config.systemSettings),
    ).kind;
    return kind !== 'test';
  }, [maintSwitches.testCustomersOnly, data, cifId, config.systemSettings]);
  const [tab, setTab] = useState<TabId>('products');
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [addressOpen, setAddressOpen] = useState<boolean>(false);
  const [saveOk, setSaveOk] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const header = useMemo(() => pickPrimaryName(data), [data]);

  const insiderCode = useMemo((): string | undefined => {
    if (!data) return undefined;
    return data.insiderCode || undefined;
  }, [data]);

  // Adapter: ParsedCustInq → RaoPerson, recomputed whenever the CIF reloads.
  const personForDialog = useMemo<RaoPerson | null>(
    () => (data ? custInqToRaoPerson(data) : null),
    [data],
  );

  // Save handler — ports CifEditPanel's handleConfirm cascade into the unified
  // dialog flow.
  //
  // officerCode here means "the officer DOING this edit" — it is NOT written to
  // Field 14 (see the raoPersonToCustMod call below: Field 14 is the CIF's
  // assigned officer and a maintenance edit must not reassign it).
  //
  // It survives for one thing that genuinely is about the editor:
  // IdVerifyAdd.IdVerifyBy — who verified the ID, a CIP record under
  // 31 CFR 1020.220 — plus the audit-note text. It was hardcoded '000' behind a
  // stale "until AuthContext surfaces it" comment; AuthContext has exposed
  // officerCode for some time (ProductsTab.tsx:232 already reads it).
  //
  // Fail CLOSED when it's unknown: stamping a placeholder into a CIP
  // verification record is exactly the bug this replaced.
  const { user } = useAuth();
  const officerCode = user?.officerCode ?? '';

  // GAP (Bill 2026-07-15 — "the Editing Operator is still not being passed"):
  // CD's Account History "Operator" column reads "0 - System" because nothing
  // supplies jXchange's auditUsrId. The server-side wire is already correct
  // (CustMod forwards `auditUsrId: req.OperatorNumber`), and CustModRequest now
  // carries OperatorNumber — but AuthUser does NOT expose one, and no
  // /v3/config/user-roles | /v3/session/my-context route returns it. The data is
  // also mostly absent: dbo.Users has an OperatorNumber column with exactly
  // 1 of 62 rows populated (connor.lang = 45) vs 57 with an OfficerCode.
  //
  // So this stays empty ON PURPOSE rather than substituting officerCode: CD
  // expects an operator NUMBER, and feeding it initials would be another
  // plausible-looking value written into an audit field — the same mistake as
  // '000'. Closing this needs (1) real operator numbers in dbo.Users and
  // (2) surfacing OperatorNumber on AuthUser. Tracked with Bill.
  const operatorNumber = '';

  const handleSave = useCallback(
    async (edited: RaoPerson): Promise<void> => {
      if (!data || !cifId) return;

      if (!officerCode) {
        setSaveErr(
          'Your officer code is missing, so this change cannot be attributed. ' +
          'Core Director records the officer on the CIF and on the ID verification, ' +
          'and saving without one would overwrite the existing officer. ' +
          'Ask an administrator to set your officer code, then try again.',
        );
        return;
      }

      setSubmitting(true);
      setSaveErr(null);
      setSaveOk(null);

      // No reassignOfficerCode: this dialog edits a customer, it does not
      // reassign their officer. Field 14 stays whatever Core Director already
      // has (Bill 2026-07-15). operatorNumber carries WHO edited — see the
      // AuthUser gap noted where officerCode is read.
      const custMod = raoPersonToCustMod(edited, data, { operatorNumber });
      const changed = changedCustModKeys(custMod);

      // The 2nd ID (CD F814/801/813/815/816) is INVISIBLE to this diff: CustMod
      // has no ID2 fields, and ParsedCustInq doesn't surface them either, so
      // `changed` can never mention them and there's no baseline to compare
      // against. Anything in extra.*2 is therefore data the officer just entered.
      //
      // This must be checked BEFORE the no-changes early-return: adding ONLY a
      // 2nd ID produces zero changed CustMod keys, so without this the save
      // would report "No changes to save." and silently write nothing.
      const editedExtra = (edited.extra ?? {}) as Record<string, unknown>;
      const x2 = (k: string): string | undefined => {
        const v = editedExtra[k];
        return typeof v === 'string' && v.trim() ? v.trim() : undefined;
      };
      // Needs a real type code (0 = Blank) + a number, mirroring the server guard.
      const idType2 = x2('idType2');
      const hasSecondId = Boolean(idType2 && idType2 !== '0' && x2('idNumber2'));

      if (changed.length === 0 && !hasSecondId) {
        setSubmitting(false);
        setEditOpen(false);
        // Bill 2026-10-17 smoke test: an edit to a field this screen does not send to Core Director (employment
        // status, for one, follows employer and occupation) read "No changes to save." Say what actually happened.
        const touched = personForDialog !== null && comparable(edited) !== comparable(personForDialog);
        if (touched) {
          setSaveErr('Nothing was saved. The fields you changed are not ones this screen writes to Core Director (employment status, for one, follows employer and occupation). Change them in Core Director.');
        } else {
          setSaveOk('No changes to save.');
        }
        return;
      }

      // Pseudo-diff for ChangeOfContact orchestrator — it expects a
      // { field, label, from, to } shape. Map CustMod keys to their strings.
      const diff: ChangeDiffEntry[] = changed.map((k) => ({
        field: String(k),
        label: String(k),
        from: '', // originalInq passthrough happens inside orchestrator via baseline
        to: String((custMod as unknown as Record<string, unknown>)[k as string] ?? ''),
      }));

      try {
        // 1. CustMod — SKIP when nothing CustMod-shaped changed (i.e. a 2nd-ID-
        //    only edit). OffCode rides on every CustMod and Core Director writes
        //    it to Field 14, so firing a "no changes" CustMod would touch the
        //    CIF's officer assignment for no reason. Cheapest correct call is not
        //    to make it.
        // BACKLOG-1675 — confirm mode: the profile-change ceremony owns name /
        // DOB / address / phones / email / employment (diff → confirm → CustMod +
        // account waterfall + notes). Fields outside that set (gender, NAICS,
        // citizenship, suffix, second email) still go through the direct CustMod
        // below, trimmed to what the ceremony did not cover.
        let profileApplied = false;
        // One banner per save (Bill 2026-10-17: "Saved" and "Save failed" showed together on a partial save).
        let partial = false;
        let residual = custMod;
        if (profileMode === 'confirm' && changed.length > 0) {
          const outcome = await beginProfileChange(edited, { source: 'rcm-edit-cif', surface: 'cif' });
          if (!outcome.proceed) { setSubmitting(false); return; } // dialog stays open; nothing written
          profileApplied = outcome.applied;
          if (outcome.notice && outcome.notice.tone !== 'ok') { setSaveErr(outcome.notice.text); partial = true; }
          if (profileApplied) {
            const trimmed = { ...custMod } as Record<string, unknown>;
            for (const k of PROFILE_CHANGE_CUSTMOD_KEYS) delete trimmed[k];
            trimmed['AuditNotesEnabled'] = false; // the ceremony wrote the notes
            residual = trimmed as unknown as typeof custMod;
          }
        }
        const residualKeys = changedCustModKeys(residual).filter((k) => String(k) !== 'AuditNotesEnabled' && String(k) !== 'OperatorNumber');
        if (residualKeys.length > 0) {
          const resp = await client.accounts.custMod(residual);
          const anyResp = resp as unknown as { success?: boolean; error?: string };
          if (anyResp?.success === false) {
            throw new Error(anyResp.error ?? 'CustMod failed');
          }
        }

        // 2. IdVerifyAdd (CIP re-verification) if any ID field changed.
        const idChanged = changed.some((k) => ID_CUSTMOD_KEYS.includes(k));
        if (idChanged) {
          try {
            const toCore = (iso: string): string => (iso || '').replace(/-/g, '');
            const idCodeRaw = (custMod.IdTypeCode ?? '1') as string;
            const idCode = (idCodeRaw || '1') as '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8';
            await client.accounts.idVerifyAdd({
              CifId: cifId,
              IdVerifyCode: idCode,
              IdVerifyVal: custMod.IdNumber ?? '',
              IdIssueBy: custMod.IdState ?? '',
              IdVerifyBy: officerCode,
              AcctType: null,
              IdIssueDt: toCore(custMod.IdIssueDate ?? ''),
              IdExpireDt: toCore(custMod.IdExpiryDate ?? ''),
            });
            emit({
              eventType: 'cif.cip.reverified',
              eventCategory: 'cif-account',
              payload: {
                cifId,
                fieldsChanged: changed.filter((k) => ID_CUSTMOD_KEYS.includes(k)).map(String),
              },
            });
          } catch (cipErr) {
            // Non-fatal: CustMod already landed.
            const cipMsg = cipErr instanceof Error ? cipErr.message : String(cipErr);
            setSaveErr(`Saved, but the ID re-verification could not be written: ${cipMsg}`);
            partial = true;
          }
        }

        // 2b. SECOND ID (CD F814/801/813/815/816) — its OWN step, deliberately.
        //
        //  · NOT on CustMod: CustModRequest has no ID2 fields (its only "2"
        //    fields are StreetAddr2/Email2).
        //  · NOT a 2nd idVerifyAdd: that call is probe-proven SINGLE-SLOT and
        //    OVERWRITES the F254-258 primary block. Bill hit precisely this on
        //    2026-07-15 — a 2nd ID entered here rewrote the primary ID's number
        //    and both dates, because the maintenance path had no ID2 route and
        //    the values fell through to the primary slot.
        //  · NOT nested under `idChanged`: that flag tracks PRIMARY ID fields
        //    only, so a 2nd-ID-only edit would never fire it.
        //
        // Routes through the same service method as cart commit
        // (AccountOpeningService.WriteSecondaryCipIdAsync), which owns the one
        // canonical field mapping so maintenance and commit cannot drift.
        // Server no-ops when there's no usable ID2, so this is idempotent.
        if (hasSecondId) {
          try {
            await client.accounts.secondaryIdWrite({
              CifId: cifId,
              IdType2: idType2,
              IdNumber2: x2('idNumber2'),
              IdIssuer2: x2('idIssuer2'),
              IdIssueDate2: x2('idIssueDate2'),
              IdExpiryDate2: x2('idExpiryDate2'),
              OffCode: officerCode,
            });
            emit({
              eventType: 'cif.cip.secondIdWritten',
              eventCategory: 'cif-account',
              payload: { cifId, idType2: idType2 ?? '' },
            });
          } catch (id2Err) {
            // Non-fatal, and say so precisely: the primary ID is intact and the
            // demographics landed — only the 2nd ID didn't. A vague "save failed"
            // here would invite a retry that re-runs idVerifyAdd for no reason.
            const id2Msg = id2Err instanceof Error ? id2Err.message : String(id2Err);
            setSaveErr(`Saved, but the second ID could not be written: ${id2Msg}`);
            partial = true;
          }
        }

        // 3. Audit note: written server-side by the profile-change ceremony, or by CustMod (AccountOpeningService,
        //    which names the fields it changed). The old client-side CustMsgAdd here used a request shape the new
        //    platform rejects (HTTP 500 on every save) and was removed 2026-10-17.

        // 4. ChangeOfContact side-effects (only if contact fields changed).
        const contactChanged = changed.some((k) => CONTACT_CUSTMOD_KEYS.includes(k));
        if (contactChanged) {
          // Build flat baseline/after maps matching the orchestrator contract.
          const before: Record<string, string> = {
            StreetAddr1: data.addr,
            City: data.city,
            State: data.state,
            Zip: data.zip,
            PhoneHome: data.phoneHome,
            PhoneCell: data.phoneCell,
            PhoneBus: data.phoneBus,
            Email: data.email,
          };
          const after: Record<string, string> = { ...before };
          for (const k of changed) {
            const v = (custMod as unknown as Record<string, unknown>)[k as string];
            if (typeof v === 'string') after[String(k)] = v;
          }
          await orchestrator.runContactChangeEffects({
            cifId,
            before,
            after,
            officerCode,
            diff,
          });
        }

        // 5. Session events.
        emit({
          eventType: 'cif.field.changed',
          eventCategory: 'cif-account',
          payload: {
            cifId,
            fields: changed.map(String),
          },
        });
        emit({
          eventType: 'cif.updated',
          eventCategory: 'cif-account',
          payload: {
            cifId,
            fields: changed.map(String),
            cipReverified: idChanged,
          },
        });

        if (!partial) {
          setSaveOk(
            `${changed.length} field${changed.length === 1 ? '' : 's'} saved to Core Director.`,
          );
        }
        setEditOpen(false);
        reload();
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setSaveErr(msg);
      } finally {
        setSubmitting(false);
      }
    },
    // officerCode/operatorNumber are now reactive (AuthContext) rather than
    // module constants — they MUST be deps or handleSave closes over a stale
    // value and silently reintroduces the placeholder it just stopped writing.
    [cifId, client, data, emit, orchestrator, reload, officerCode, operatorNumber, beginProfileChange, profileMode, personForDialog],
  );

  if (!cifId) {
    return <EmptyState title="CIF id missing" message="Open a record from the search list." />;
  }
  if (loading) {
    return (
      <div style={{ padding: 'var(--sp-9)', display: 'flex', justifyContent: 'center' }}>
        <LoadingSpinner label="Loading CIF…" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <EmptyState
        title="Unable to load CIF"
        message={error ?? 'Record not found.'}
        action={<Button variant="primary" onClick={reload}>Retry</Button>}
      />
    );
  }

  return (
    <div className="rcm-record">
      <header className="rcm-record__header">
        <EntityIcon type="relationship" code="CF" title="CIF record" size={32} />
        <div style={{ flex: 1 }}>
          <h1 className="rcm-record__title">{header.name}</h1>
          <div className="rcm-record__meta">
            CIF <code style={{ fontFamily: 'var(--font-mono)' }}>{cifId}</code>
            {' · '}SSN {formatSsn(header.ssn)}
          </div>
          <div style={{ marginTop: 'var(--sp-5)' }}>
            <RelatedPeople cifId={cifId} />
          </div>
          <div style={{ marginTop: 'var(--sp-4)' }}>
            <InsiderMonitor insiderCode={insiderCode} variant="compact" />
          </div>
        </div>
        {/*
          Header actions (Bill 2026-07-15, ahead of the Friday retail walk-through).
          These were three buttons stacked vertically in three DIFFERENT
          treatments — a ghost "← Back to search", a solid primary "Edit CIF", and
          a ghost "Change Address" — so navigation and actions read as one
          undifferentiated pile and nothing announced itself as the main thing.

          Now: navigation sits ABOVE, as a link, because going back is not an
          action on this customer. The two actions sit together on one row, with
          exactly one primary — "Edit CIF" is the reason an officer is on this
          page; "Change Address" is a specialised shortcut into the same record.
        */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', alignItems: 'flex-end' }}>
          <Button variant="link" size="sm" onClick={() => navigate('/rcm')}>← Back to search</Button>
          {/* Account maintenance switches (Bill 2026-10-17: "turn off by admin toggle"). Edit CIF writes to Core
              (profile change); Change Address is the older dialog that doesn't reach Core correctly. */}
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            {maintSwitches.legacyRcmActions && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSaveOk(null);
                  setSaveErr(null);
                  if (testGateBlocks) { setSaveErr(TEST_GATE_MESSAGE); return; }
                  setAddressOpen(true);
                }}
              >
                Change Address
              </Button>
            )}
            {maintSwitches.profileEdit && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setSaveOk(null);
                  setSaveErr(null);
                  if (testGateBlocks) { setSaveErr(TEST_GATE_MESSAGE); return; }
                  setEditOpen(true);
                }}
                disabled={!personForDialog}
              >
                Edit CIF
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Save result banners live OUTSIDE the modal so the officer sees them */}
      {/* when they land back on the header strip after a successful save. */}
      {saveOk && (
        <Banner tone="ok" title="Saved">
          {saveOk} <span style={{ fontFamily: 'var(--font-mono)' }}>{formatLocal(new Date())}</span>
        </Banner>
      )}
      {saveErr && <Banner tone="err" title="Save failed">{saveErr}</Banner>}
      {submitting && (
        <Banner tone="info" title="Saving…">
          Posting changes to Core Director…
        </Banner>
      )}

      <nav className="rcm-record__tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={t.id === tab}
            className={`rcm-record__tab ${t.id === tab ? 'is-active' : ''}`.trim()}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div role="tabpanel">
        {tab === 'products'  && <ProductsTab cifId={cifId} testGateBlocks={testGateBlocks} />}
        {tab === 'documents' && <DocumentsTab cifId={cifId} />}
        {tab === 'tasks'     && <TasksTab cifId={cifId} />}
        {tab === 'history'   && <HistoryTab cifId={cifId} />}
      </div>

      {/* Edit CIF modal — dispatcher routes to the correct category dialog. */}
      {profileChangeModal}

      {personForDialog && (
        <PersonDialog
          open={editOpen}
          mode="edit"
          person={personForDialog}
          onSave={(edited) => { void handleSave(edited); }}
          onClose={() => { if (!submitting) setEditOpen(false); }}
          cartId={cifId}
          officerCode={officerCode}
        />
      )}

      {/* Streamlined CIF-level Address Change — sibling of Edit CIF so
          high-frequency address work doesn't drag the officer through the
          full demographic surface. Ports the legacy Change-of-Contact flow. */}
      <AddressChangeDialog
        open={addressOpen}
        onClose={() => { setAddressOpen(false); }}
        onSaved={() => { reload(); setSaveOk('Address updated.'); }}
        cifId={cifId}
        officerCode={officerCode}
        currentAddress={{
          addr: data.addr,
          city: data.city,
          state: data.state,
          zip: data.zip,
        }}
      />
    </div>
  );
}
